import { filter, reduce, each, get, isObject, parseInt, isInteger, merge, isArray, isEmpty } from 'lodash'
import Moment from 'moment'
import Vue from 'vue'

const QueryParamMixin = {
  data () {
    return {
      queryParams: []
    }
  },
  methods: {
    /**
     * Reset the route path back to the default with no query params. Currently, uses `$router.replace` and does not
     * create a new history entry.
     * @returns {Promise<Route>}
     */
    resetQueryParams () {
      return this.$router.replace({ path: this.$route.path }).catch(error => {
        if (error.name !== 'NavigationDuplicated') {
          throw error
        }
      })
    },
    /**
     * Convenience method to trigger merging the query params with the searchCriteria property.
     * Merging the query params doesn't cause a reactivity change, so this will merge and run `Vue.set` on the
     * searchCriteria property.
     */
    useQueryParamsForSearch () {
      Vue.set(this.searchCriteria, merge(this.searchCriteria, this.getQueryParams()))
    },
    /**
     * Pull the query params and turn them into an object that can be used as search criteria.
     * The format for parameter entries in this.queryParams are in this form:
     * {
     *   name: 'paramName', // Required: the query param, should match the value used in searchCriteria.
     *   type: 'SomeType',  // Optional: See below for types supported
     *   validator: fn      // Optional: validation defined here
     * },
     * @returns {{}}
     */
    getQueryParams () {
      const results = {}
      each(this.queryParams, param => {
        let result
        const paramValue = get(this.$route.query, param.name)
        if (paramValue != null) {
          // Integer Array, parse to integer then validate
          if (param.type === 'IDArray') {
            result = reduce(paramValue.split(','), (acc, x) => {
              const temp = parseInt(x)
              if (isInteger(temp)) {
                if (param.validator == null || param.validator(temp)) {
                  acc.push(temp)
                }
              }
              return acc
            }, [])
          // String Array, validate each entry.
          } else if (param.type === Array) {
            result = filter(paramValue.split(','), x => {
              return param.validator == null || param.validator(x)
            })
          // Date parseable by Moment, no other validation performed.
          } else if (param.type === 'Moment') {
            const value = Moment(paramValue)
            if (value.isValid()) {
              result = value.format()
            }
          // A single integer, need to convert from string to int
          } else if (param.type === 'Integer') {
            const temp = parseInt(paramValue)
            if (isInteger(temp)) {
              if (param.validator == null || param.validator(temp)) {
                result = temp
              }
            }
          // Any other type, but assumed to be a String. All we do here is check against the validator.
          } else {
            if (param.validator == null || param.validator(paramValue)) {
              result = paramValue
            }
          }
        }
        if (result != null) {
          results[param.name] = result
        }
      })
      return results
    },
    /**
     * Build a query params url string from the current state.
     * This seems like it would be re convenient to have as a computed property, but I'm noticing that not all
     * searchCriteria properties across the pages will trigger a change on searchCriteria itself, regardless of whether
     * it's used in a property or if we try to watch it in `watch`.
     * @returns {string}
     */
    buildQueryParamsString () {
      const results = []
      each(this.queryParams, param => {
        const paramValue = get(this.searchCriteria, param.name)
        if (isArray(paramValue)) {
          if (paramValue.length > 0) {
            results.push(`${param.name}=${paramValue.join(',')}`)
          }
        } else if (paramValue != null) {
          results.push(`${param.name}=${paramValue}`)
        }
      })
      if (results.length > 0) {
        return '?' + results.join('&')
      }
    }
  }
}

export default QueryParamMixin
